import { useState } from 'react';
import { Button, Divider, Form, Input, InputNumber, Select, Upload } from 'antd';
import { CheckOutlined, DeleteOutlined, FileAddOutlined, FileDoneOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch } from '@hooks/redux';
import { createLoanRequestApi } from '@http/loans';
import {
  loanProductTypes,
  loanRequestCurrencies,
  loanRequestDocuments,
  LOAN_REQUEST_FILE_MAX_MB,
  LOAN_REQUEST_FILE_ACCEPT,
  LOAN_REQUEST_FILE_FORMATS,
} from '@assets/constants/loanRequestOptions';
import digitNumber from '@utils/digitNumber';
import { formatDate } from '@utils/formatDate';
import openNotification from '@utils/openNotification';

const { TextArea } = Input;

function LoanApplicationCreate() {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [stage, setStage] = useState('form');
  const [requestValues, setRequestValues] = useState<any>({});
  const [documents, setDocuments] = useState<any>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const productTypeLabel = (value: string) => {
    const product = loanProductTypes.find((item: any) => item.value === value);
    return product ? t(product.labelKey) : value;
  };

  const attachedDocuments = loanRequestDocuments.filter((item: any) => documents[item.name]);

  const addDocumentHandler = (name: string, file: any) => {
    if (file.size > LOAN_REQUEST_FILE_MAX_MB * 1024 * 1024) {
      openNotification('error', {
        code: '',
        message: 'Ошибка',
        detail: `${t('File is too large')} (${LOAN_REQUEST_FILE_MAX_MB} ${t('MB')})`,
      });
      return;
    }
    setDocuments({ ...documents, [name]: file });
  };

  const removeDocumentHandler = (name: string) => {
    setDocuments({ ...documents, [name]: null });
  };

  const goToPreview = () => {
    form.validateFields().then((values: any) => {
      setRequestValues(values);
      setStage('preview');
      window.scrollTo(0, 0);
    });
  };

  const backToForm = () => {
    setStage('form');
    window.scrollTo(0, 0);
  };

  const submit = () => {
    setIsSubmitting(true);

    const payload = {
      ...requestValues,
      amount: String(requestValues.amount),
      term: String(requestValues.term),
    };

    const files: any = {};
    loanRequestDocuments.forEach((item: any, index: number) => {
      if (documents[item.name]) {
        files[`file${index + 1}`] = documents[item.name];
      }
    });

    dispatch(createLoanRequestApi(payload, files))
      .then((data: any) => {
        if (data) {
          setStage('success');
          window.scrollTo(0, 0);
        }
      })
      .finally(() => setIsSubmitting(false));
  };

  return (
    <div className='flex justify-center'>
      <div className='lg:w-[900px] w-full'>
        <div className='flex'>
          <div className='py-4 lg:py-8 w-full h-full bg-white mb-12'>
            <p className='text-sm text-gray-400 mb-2'>
              {t('Loans')} &nbsp;&rarr;&nbsp; {t('Submit request')}
            </p>
            <h1 className='text-xl font-medium color-active mb-7'>
              {stage === 'preview' ? t('Request preview title') : t('Request create title')}
            </h1>

            {stage === 'form' ? (
              <div>
                <p className='text-base color-active font-semibold mb-4'>{t('Deal parameters')}</p>

                <Form
                  form={form}
                  name='loanRequest'
                  layout='vertical'
                  initialValues={{ ccy: 'TJS', loan_type: 'Инкишоф' }}
                >
                  <Form.Item
                    label={t('Loan product type')}
                    name='loan_type'
                    rules={[{ required: true, message: `${t('Required')}` }]}
                  >
                    <Input size='large' disabled />
                  </Form.Item>

                  <div className='flex gap-3'>
                    <div className='flex-[2]'>
                      <Form.Item
                        label={t('Financing amount')}
                        name='amount'
                        rules={[{ required: true, message: `${t('Required')}` }]}
                      >
                        <InputNumber
                          style={{ width: '100%' }}
                          size='large'
                          min={0}
                          controls={false}
                          formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                          parser={value => value?.replace(/\s/g, '') as any}
                        />
                      </Form.Item>
                    </div>
                    <div className='flex-1'>
                      <Form.Item
                        label={t('Currency')}
                        name='ccy'
                        rules={[{ required: true, message: `${t('Required')}` }]}
                      >
                        <Select size='large' options={loanRequestCurrencies} />
                      </Form.Item>
                    </div>
                  </div>

                  <div className='flex gap-3'>
                    <div className='flex-1'>
                      <Form.Item
                        label={t('Desired term in months')}
                        name='term'
                        rules={[{ required: true, message: `${t('Required')}` }]}
                      >
                        <InputNumber style={{ width: '100%' }} size='large' min={1} max={120} />
                      </Form.Item>
                    </div>
                  </div>

                  <Form.Item
                    label={t('Financing purpose')}
                    name='purpose'
                    rules={[{ required: true, message: `${t('Required')}` }]}
                  >
                    <TextArea rows={3} size='large' maxLength={200} />
                  </Form.Item>

                  <Divider />
                  <p className='text-base color-active font-semibold mb-4'>{t('Applicant data')}</p>

                  <Form.Item
                    label={t('Director full name')}
                    name='fullname'
                    rules={[{ required: true, message: `${t('Required')}` }]}
                  >
                    <Input size='large' />
                  </Form.Item>

                  <Form.Item
                    label={t('Phone number')}
                    name='phone_number'
                    rules={[{ required: true, message: `${t('Required')}` }]}
                  >
                    <Input size='large' />
                  </Form.Item>

                  <Form.Item
                    label={t('Email')}
                    name='email'
                    rules={[
                      { required: true, message: `${t('Required')}` },
                      { type: 'email', message: `${t('Invalid email')}` },
                    ]}
                  >
                    <Input size='large' />
                  </Form.Item>
                </Form>

                <Divider />
                <div className='flex items-center justify-between mb-1'>
                  <p className='text-base color-active font-semibold'>{t('Additional documents')}</p>
                  <p
                    className='text-xs font-semibold px-3 py-1 rounded-full'
                    style={{ backgroundColor: '#f0f2f5', color: '#011957' }}
                  >
                    {attachedDocuments.length} / {loanRequestDocuments.length}
                  </p>
                </div>
                <div
                  className='mt-4 mb-5 px-4 py-3 rounded-md'
                  style={{ backgroundColor: '#f7fafc', border: '1px solid #e2e8f0' }}
                >
                  <p className='text-xs font-semibold uppercase mb-2' style={{ color: '#4a5568' }}>
                    {t('Recommended documents list')}:
                  </p>
                  <p className='text-xs leading-relaxed' style={{ color: '#4a5568' }}>
                    {loanRequestDocuments
                      .map((item: any, index: number) => `${index + 1}. ${t(item.labelKey)}`)
                      .join('; ')}
                    .
                  </p>
                </div>

                {loanRequestDocuments.map((item: any, index: number) => {
                  const file = documents[item.name];

                  return (
                    <div
                      key={item.name}
                      className={`flex items-center justify-between gap-4 p-4 mb-3 rounded-lg ${
                        file ? '' : 'listItem'
                      }`}
                      style={{
                        border: `1px solid ${file ? '#b7eb8f' : '#eee'}`,
                        backgroundColor: file ? '#f6ffed' : undefined,
                      }}
                    >
                      <div className='flex items-center gap-4 min-w-0'>
                        <div
                          className='h-9 w-9 shrink-0 rounded-full flex items-center justify-center text-sm font-semibold'
                          style={{
                            backgroundColor: file ? '#52c41a' : '#f0f2f5',
                            color: file ? '#fff' : '#011957',
                          }}
                        >
                          {file ? <CheckOutlined /> : index + 1}
                        </div>
                        <div className='min-w-0'>
                          <p className='text-sm font-medium color-active truncate'>{t(item.labelKey)}</p>
                          {file ? (
                            <p className='text-xs text-gray-500 truncate mt-1'>
                              <FileDoneOutlined className='mr-1' />
                              {file.name} &middot; {(file.size / 1024 / 1024).toFixed(2)} {t('MB')}
                            </p>
                          ) : (
                            <p className='text-xs text-gray-400 mt-1'>
                              {LOAN_REQUEST_FILE_FORMATS} &middot; {t('up to')} {LOAN_REQUEST_FILE_MAX_MB} {t('MB')}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className='shrink-0'>
                        {file ? (
                          <Button
                            type='text'
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => removeDocumentHandler(item.name)}
                          />
                        ) : (
                          <Upload
                            maxCount={1}
                            showUploadList={false}
                            beforeUpload={() => false}
                            accept={LOAN_REQUEST_FILE_ACCEPT}
                            onChange={e => addDocumentHandler(item.name, e.file)}
                          >
                            <Button icon={<FileAddOutlined />}>{t('Select file')}</Button>
                          </Upload>
                        )}
                      </div>
                    </div>
                  );
                })}

                <div className='flex justify-end gap-3 mt-7'>
                  <Button size='middle' onClick={() => navigate('/loans/applications')}>
                    {t('Cancel')}
                  </Button>
                  <Button type='primary' size='middle' onClick={goToPreview}>
                    {t('Generate preview')}
                  </Button>
                </div>
              </div>
            ) : null}

            {stage === 'preview' ? (
              <div>
                <div className='p-6 lg:p-12 text-sm leading-relaxed ' style={{ border: '1px solid #eee' }}>
                  <div className='md:ml-[45%] mb-10 text-end'>
                    Председателю Правления
                    <br />
                    ОАО «Ориёнбанк»
                    <br />
                    уважаемому Хасану Асадуллозода
                    <br />
                    Контакты:{' '}
                    <span className='font-semibold'>{`${requestValues.phone_number}, ${requestValues.email}`}</span>
                  </div>

                  <p className='text-center font-semibold uppercase my-7'>
                    Заявление-намерение (LOI)
                    <br />
                    на получение финансирования
                  </p>

                  <div className='text-justify mb-10'>
                    <p className='mb-5'>Уважаемый Хасан Асадуллозода,</p>
                    <p className='mb-3'>
                      Настоящим просим Вас рассмотреть возможность предоставления финансирования на следующих
                      предварительных условиях:
                    </p>
                    <ul className='list-none pl-5 my-3'>
                      <li className='mb-1'>
                        1. Вид продукта:{' '}
                        <span className='font-semibold'>{productTypeLabel(requestValues.loan_type)}</span>
                      </li>
                      <li className='mb-1'>
                        2. Запрашиваемая сумма и валюта:{' '}
                        <span className='font-semibold'>
                          {digitNumber(requestValues.amount)} {requestValues.ccy}
                        </span>
                      </li>
                      <li className='mb-1'>
                        3. Желаемый срок финансирования: <span className='font-semibold'>{requestValues.term}</span>{' '}
                        месяцев
                      </li>
                      <li className='mb-1'>
                        4. Целевое назначение средств: <span className='font-semibold'>{requestValues.purpose}</span>
                      </li>
                    </ul>
                    <p className='mt-3'>
                      Настоящим подтверждаем, что вся информация, предоставленная в данной экспресс-анкете, является
                      достоверной. Мы выражаем готовность предоставить полный пакет юридических и финансовых документов
                      по первому запросу специалистов Банка в случае положительного заключения по результатам
                      предварительного анализа.
                    </p>
                  </div>

                  <div className='mt-7 pt-4' style={{ borderTop: '1px solid #eee' }}>
                    С уважением,
                    <br />
                    Генеральный директор: <span className='font-semibold'>{requestValues.fullname}</span>
                    <br />
                    Дата формирования заявки: <span className='font-semibold'>{formatDate(new Date())}</span> г.
                    <p className='text-gray-400 italic mt-3'>Сформировано автоматически в системе ДБО «Ориён Бизнес»</p>
                  </div>
                </div>

                <div className='mt-5 p-5 rounded-lg' style={{ border: '1px solid #eee' }}>
                  <p className='text-sm font-semibold color-active mb-3'>{t('Attached documents')}</p>
                  {attachedDocuments.length > 0 ? (
                    <ul className='list-disc pl-5 text-sm text-green-600'>
                      {attachedDocuments.map((item: any) => (
                        <li key={item.name} className='mb-1'>
                          {t(item.labelKey)}: {documents[item.name].name}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className='text-sm text-gray-400'>{t('No documents attached')}</p>
                  )}
                </div>

                <div className='flex justify-end gap-3 mt-7'>
                  <Button size='middle' onClick={backToForm}>
                    {t('Back to edit')}
                  </Button>
                  <Button type='primary' size='middle' onClick={submit} loading={isSubmitting}>
                    {t('Send request to bank')}
                  </Button>
                </div>
              </div>
            ) : null}

            {stage === 'success' ? (
              <div className='py-12 flex flex-col items-center text-center'>
                <div className='h-16 w-16 rounded-full bg-green-100 text-green-600 flex items-center justify-center mb-5'>
                  <CheckOutlined style={{ fontSize: '28px' }} />
                </div>
                <h3 className='text-xl font-medium color-active mb-3'>{t('Request submitted')}</h3>
                <p className='text-gray-400 mb-7'>{t('Request submitted desc')}</p>
                <Button type='primary' size='middle' onClick={() => navigate('/loans/applications')}>
                  {t('Back to requests')}
                </Button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoanApplicationCreate;
